import React from 'react';
import { useMutation } from 'react-query';
import { Trans, useTranslation } from 'react-i18next';
import { useRequest } from '@datahub/api-client';
import { ApiOperation } from '../../../../../api';
import { UpdateAllowUsersToSeeOnlyInvoicesUploadedByThemPayloadInterface } from './interfaces/UpdateAllowUsersToSeeOnlyInvoicesUploadedByThemPayload.interface';
import { UpdateUploadHubSettingsMutationPropsInterface } from './interfaces/UpdateUploadHubSettingsMutationProps.interface';
import useGeneralErrorHandler from '../../../../error-handlers/useGeneralErrorHandler';
import useOnError from '../../../../error-handlers/useOnError';
import { queryClient } from '../../../../../queryClient';
import { getUploadHubSettingsQueryKey } from '../../../../queries/queryKeys';
import useDebouncedMutation from '../../../useDebounced.mutation';
import { useAlert } from '../../../../../alert/Alert.provider';

const useUpdateAllowUsersToSeeOnlyInvoicesUploadedByThemMutation = ({
  setOtherError,
}: UpdateUploadHubSettingsMutationPropsInterface = {}) => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const { request } = useRequest(ApiOperation.UpdateAllowUsersToSeeOnlyInvoicesUploadedByThem);

  const generalErrorHandler = useGeneralErrorHandler(setOtherError);
  const { onError } = useOnError([], generalErrorHandler);

  const { mutate, ...rest } = useMutation(
    async (payload: UpdateAllowUsersToSeeOnlyInvoicesUploadedByThemPayloadInterface) => {
      const res = await request({ data: { ...payload } });

      return {
        data: res.data,
        payload,
      };
    },
    {
      onSuccess: (
        _,
        {
          allowUsersToSeeOnlyInvoicesUploadedByThem,
        }: UpdateAllowUsersToSeeOnlyInvoicesUploadedByThemPayloadInterface,
      ) => {
        queryClient.invalidateQueries(getUploadHubSettingsQueryKey);

        showAlert(
          <Trans
            i18nKey="pluginSettings.sections.uploadHub.subsections.general.controls.allowUsersToSeeOnlyInvoicesUploadedByThem.success"
            values={{
              status: allowUsersToSeeOnlyInvoicesUploadedByThem
                ? t('global.enabled')
                : t('global.disabled'),
            }}
            components={{ bold: <strong /> }}
          />,
          allowUsersToSeeOnlyInvoicesUploadedByThem ? 'success' : 'info',
        );
      },
      onError,
    },
  );

  const { mutateDebounced } =
    useDebouncedMutation<UpdateAllowUsersToSeeOnlyInvoicesUploadedByThemPayloadInterface>(mutate);

  return {
    mutate,
    mutateDebounced,
    ...rest,
  };
};

export default useUpdateAllowUsersToSeeOnlyInvoicesUploadedByThemMutation;
